import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { CalendarClock, Send, Zap, X, Plus } from "lucide-react";
import { toast } from "sonner";

const toIso = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  try {
    const d = new Date(`${dateStr}T${timeStr}:00`);
    if (isNaN(d.getTime())) return null;
    return d.toISOString();
  } catch { return null; }
};

const fmtLabel = (dateStr, timeStr) => {
  const iso = toIso(dateStr, timeStr);
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return "—"; }
};

export default function ScheduleClientDialog({ client, team, onDone }) {
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState({ operation_types: [] });
  const [mode, setMode] = useState("propose"); // "propose" | "direct"
  const [assignedUserId, setAssignedUserId] = useState("");
  const [operationType, setOperationType] = useState("");
  const [duration, setDuration] = useState(30);
  const [notes, setNotes] = useState("");
  const [requiresOnCall, setRequiresOnCall] = useState(false);
  const [busy, setBusy] = useState(false);
  const [proposedRows, setProposedRows] = useState([blankRow(), blankRow(), blankRow()]);
  const [directDate, setDirectDate] = useState("");
  const [directTime, setDirectTime] = useState("");

  function blankRow() {
    return { date: "", time: "" };
  }

  useEffect(() => {
    if (!open) return;
    api.get("/scheduling/meta").then(({ data }) => {
      setMeta(data);
      if (!operationType && data.operation_types?.length) {
        setOperationType(data.operation_types[0]);
      }
    }).catch(() => {});
    // Default assigned to existing client assignment
    if (!assignedUserId && client?.assigned_team_member_id) {
      setAssignedUserId(client.assigned_team_member_id);
    }
    /* eslint-disable-next-line */
  }, [open]);

  const validProposed = useMemo(() => proposedRows
    .map((r) => ({ ...r, iso: toIso(r.date, r.time) }))
    .filter((r) => !!r.iso), [proposedRows]);

  const addRow = () => {
    if (proposedRows.length >= 8) return;
    setProposedRows([...proposedRows, blankRow()]);
  };
  const removeRow = (i) => setProposedRows(proposedRows.filter((_, idx) => idx !== i));
  const updateRow = (i, patch) => {
    const copy = [...proposedRows];
    copy[i] = { ...copy[i], ...patch };
    setProposedRows(copy);
  };

  const submitPropose = async () => {
    if (!assignedUserId) return toast.error("Pick a team member");
    if (!operationType) return toast.error("Pick what's being done");
    if (validProposed.length < 1) return toast.error("Add at least one time");
    setBusy(true);
    try {
      const options = validProposed.map((r) => ({ start: r.iso, duration_minutes: duration }));
      await api.post("/scheduling/schedules", {
        client_id: client.id,
        assigned_user_id: assignedUserId,
        operation_type: operationType,
        options,
        notes,
        requires_client_on_call: requiresOnCall,
      });
      toast.success(`Sent ${options.length} time${options.length > 1 ? "s" : ""} to ${client.name}`);
      setOpen(false);
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send times");
    } finally { setBusy(false); }
  };

  const submitDirect = async () => {
    if (!assignedUserId) return toast.error("Pick a team member");
    if (!operationType) return toast.error("Pick what's being done");
    const iso = toIso(directDate, directTime);
    if (!iso) return toast.error("Pick a date & time");
    setBusy(true);
    try {
      await api.post("/scheduling/schedules/direct", {
        client_id: client.id,
        assigned_user_id: assignedUserId,
        operation_type: operationType,
        start: iso,
        duration_minutes: duration,
        notes,
        requires_client_on_call: requiresOnCall,
      });
      toast.success(`Scheduled ${client.name} directly`);
      setOpen(false);
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to schedule");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="schedule-client-btn">
          <CalendarClock size={16} className="mr-1" /> Schedule Client
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="schedule-client-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Schedule {client?.name}</DialogTitle>
          <p className="text-sm text-slate-500">Pick a mode, assign a specialist, and choose what's being done.</p>
        </DialogHeader>

        {/* Mode toggle */}
        <div className="flex gap-2 rounded-xl bg-slate-100 p-1 mb-2">
          <button
            onClick={() => setMode("propose")}
            className={`flex-1 rounded-lg py-2 text-sm font-semibold transition ${mode === "propose" ? "bg-white text-slate-900 shadow" : "text-slate-500"}`}
            data-testid="mode-propose"
          >
            <Send size={14} className="inline mr-1" /> Send times to client
          </button>
          <button
            onClick={() => setMode("direct")}
            className={`flex-1 rounded-lg py-2 text-sm font-semibold transition ${mode === "direct" ? "bg-white text-slate-900 shadow" : "text-slate-500"}`}
            data-testid="mode-direct"
          >
            <Zap size={14} className="inline mr-1" /> Schedule directly
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Team member</Label>
            <Select value={assignedUserId} onValueChange={setAssignedUserId}>
              <SelectTrigger data-testid="schedule-assignee"><SelectValue placeholder="Pick assignee" /></SelectTrigger>
              <SelectContent>
                {team.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name} · {t.role}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>What's being done</Label>
            <Select value={operationType} onValueChange={setOperationType}>
              <SelectTrigger data-testid="schedule-operation-type"><SelectValue placeholder="Pick operation" /></SelectTrigger>
              <SelectContent>
                {(meta.operation_types || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Duration (min)</Label>
            <Input type="number" min={15} max={240} value={duration} onChange={(e) => setDuration(Math.max(15, Math.min(240, parseInt(e.target.value || "30", 10))))} data-testid="schedule-duration" />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={requiresOnCall} onChange={(e) => setRequiresOnCall(e.target.checked)} data-testid="schedule-requires-call" />
              Client must be on a call
            </label>
          </div>
        </div>

        {mode === "propose" && (
          <div className="mt-3">
            <div className="flex items-center justify-between mb-2">
              <Label>Proposed times (up to 8)</Label>
              <Button size="sm" variant="outline" onClick={addRow} disabled={proposedRows.length >= 8} data-testid="schedule-add-row">
                <Plus size={14} className="mr-1" /> Add time
              </Button>
            </div>
            <div className="space-y-2">
              {proposedRows.map((r, i) => (
                <Card key={i} className="p-3 border-slate-100 rounded-xl flex items-center gap-2" data-testid={`schedule-row-${i}`}>
                  <Input type="date" value={r.date} onChange={(e) => updateRow(i, { date: e.target.value })} className="flex-1" data-testid={`schedule-row-date-${i}`} />
                  <Input type="time" value={r.time} onChange={(e) => updateRow(i, { time: e.target.value })} className="w-32" data-testid={`schedule-row-time-${i}`} />
                  <div className="text-xs text-slate-500 min-w-[130px] hidden sm:block">{fmtLabel(r.date, r.time)}</div>
                  {proposedRows.length > 1 && (
                    <Button size="sm" variant="ghost" onClick={() => removeRow(i)} className="text-red-500" data-testid={`schedule-row-remove-${i}`}>
                      <X size={14} />
                    </Button>
                  )}
                </Card>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-2">{validProposed.length} valid time{validProposed.length !== 1 ? "s" : ""} ready to send.</p>
          </div>
        )}

        {mode === "direct" && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Date</Label>
              <Input type="date" value={directDate} onChange={(e) => setDirectDate(e.target.value)} data-testid="schedule-direct-date" />
            </div>
            <div>
              <Label>Time</Label>
              <Input type="time" value={directTime} onChange={(e) => setDirectTime(e.target.value)} data-testid="schedule-direct-time" />
            </div>
            <div className="col-span-2 text-sm text-slate-500">
              Will confirm immediately as: <span className="font-semibold text-slate-800">{fmtLabel(directDate, directTime)}</span>
            </div>
          </div>
        )}

        <div className="mt-3">
          <Label>Notes (optional)</Label>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any context for the assigned team member…" data-testid="schedule-notes" />
        </div>

        <DialogFooter className="pt-2">
          <Button variant="ghost" onClick={() => setOpen(false)} data-testid="schedule-cancel">Cancel</Button>
          {mode === "propose" ? (
            <Button onClick={submitPropose} disabled={busy || validProposed.length < 1} className="bg-amber-500 hover:bg-amber-600" data-testid="schedule-submit-propose">
              <Send size={14} className="mr-1" /> {busy ? "Sending…" : "Send times to client"}
            </Button>
          ) : (
            <Button onClick={submitDirect} disabled={busy} className="bg-amber-500 hover:bg-amber-600" data-testid="schedule-submit-direct">
              <Zap size={14} className="mr-1" /> {busy ? "Scheduling…" : "Schedule directly"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
